// Purchase and meet creation tracking utility
// Freemium model: 2 free meets, then $2.99 one-time purchase for unlimited

const STORAGE_KEY = 'foresight_purchase_data';

// ============================================
// PRICING CONFIGURATION - Adjust these values
// ============================================
export const PRICING_CONFIG = {
  FREE_MEET_LIMIT: 2,
  PREMIUM_PRICE: 2.99,
  PREMIUM_PRICE_DISPLAY: '$2.99',
  CURRENCY: 'USD',
  // Future price adjustment (after app matures)
  // Simply update PREMIUM_PRICE and PREMIUM_PRICE_DISPLAY
  // Existing premium users remain premium regardless of price changes
};

// Product IDs for App Store and Google Play
export const PRODUCT_IDS = {
  PREMIUM_UNLOCK: 'com.foresighttrackfield.premium',
  // Legacy - keep for backwards compatibility
  MEET_CREATION: 'com.foresighttrackfield.meetcreation',
};

// Get purchase data from localStorage
export const getPurchaseData = () => {
  const data = localStorage.getItem(STORAGE_KEY);
  if (!data) {
    return {
      meetsCreated: 0,
      isPremium: false,
      premiumPurchaseDate: null,
      premiumTransactionId: null,
      purchaseHistory: [],
    };
  }
  return JSON.parse(data);
};

// Save purchase data to localStorage
export const savePurchaseData = (data) => {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(data));
};

// Check if user is premium (unlimited meets)
export const isPremiumUser = () => {
  const data = getPurchaseData();
  return data.isPremium === true;
};

// Get remaining free meets
export const getFreeMeetsRemaining = () => {
  const data = getPurchaseData();
  if (data.isPremium) return Infinity; // Premium = unlimited
  return Math.max(0, PRICING_CONFIG.FREE_MEET_LIMIT - data.meetsCreated);
};

// Check if user can create a meet
export const canCreateMeet = () => {
  const data = getPurchaseData();
  // Premium users can always create
  if (data.isPremium) return true;
  // Free users have limited meets
  return data.meetsCreated < PRICING_CONFIG.FREE_MEET_LIMIT;
};

// Check if user needs to purchase
export const needsToPurchase = () => {
  return !canCreateMeet();
};

// Record a meet creation
export const recordMeetCreation = () => {
  const data = getPurchaseData();
  data.meetsCreated += 1;
  savePurchaseData(data);
  return data;
};

// Record premium purchase (called after successful IAP)
export const recordPremiumPurchase = (transactionId) => {
  const data = getPurchaseData();
  data.isPremium = true;
  data.premiumPurchaseDate = new Date().toISOString();
  data.premiumTransactionId = transactionId;
  data.purchaseHistory.push({
    type: 'premium_unlock',
    transactionId,
    date: new Date().toISOString(),
    amount: PRICING_CONFIG.PREMIUM_PRICE,
  });
  savePurchaseData(data);
  return data;
};

// Get purchase summary for display
export const getPurchaseSummary = () => {
  const data = getPurchaseData();
  const freeMeetsUsed = Math.min(data.meetsCreated, PRICING_CONFIG.FREE_MEET_LIMIT);
  const freeMeetsRemaining = data.isPremium 
    ? 0 // Premium users don't use "free" meets
    : Math.max(0, PRICING_CONFIG.FREE_MEET_LIMIT - data.meetsCreated);
  
  return {
    totalMeetsCreated: data.meetsCreated,
    freeMeetsUsed: freeMeetsUsed,
    freeMeetsRemaining: freeMeetsRemaining,
    freeMeetLimit: PRICING_CONFIG.FREE_MEET_LIMIT,
    isPremium: data.isPremium,
    premiumPrice: PRICING_CONFIG.PREMIUM_PRICE_DISPLAY,
    canCreateMeet: canCreateMeet(),
    purchaseHistory: data.purchaseHistory,
  };
};

// Reset purchase data (for testing only)
export const resetPurchaseData = () => {
  localStorage.removeItem(STORAGE_KEY);
};

// Simulate a premium purchase (for development/testing without actual IAP)
// Remove this in production when real IAP is connected
export const simulatePurchase = () => {
  const transactionId = `test-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
  return recordPremiumPurchase(transactionId);
};

// Get pricing info for display
export const getPricingInfo = () => {
  return {
    price: PRICING_CONFIG.PREMIUM_PRICE,
    priceDisplay: PRICING_CONFIG.PREMIUM_PRICE_DISPLAY,
    currency: PRICING_CONFIG.CURRENCY,
    freeMeetLimit: PRICING_CONFIG.FREE_MEET_LIMIT,
    productId: PRODUCT_IDS.PREMIUM_UNLOCK,
  };
};
